import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { forkJoin } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { Globals } from 'src/app/shared/globals/globals';
import { Attachment, EnquiryAttachment, ItemAttachment } from 'src/app/shared/models/data/enquiry.model';
import { AttachmentGroup, ItdcrAttachment, ItdcrAttachmentItem } from 'src/app/shared/models/data/itcdrAttachments';
import { BackendService } from 'src/app/shared/services/backend/backend.service';
import { TeiasoftLoaderService } from 'src/app/shared/services/teiasoft-bucket/teiasoft-loader.service';
import { TeiasoftToasterService } from 'src/app/shared/services/teiasoft-bucket/teiasoft-toaster.service';



@Component({
  selector: 'app-modal-itdcr-attachments',
  templateUrl: './modal-itdcr-attachments.component.html',
  styleUrl: './modal-itdcr-attachments.component.scss'
})
export class ModalItdcrAttachmentsComponent implements OnInit {


  @Input() itdcrNo!: string;
  @Input() isMobile: boolean = false;
  @Input() theme: string = 'silver';
  @Input() readOnly: boolean = true;


  @Output() onClose = new EventEmitter<void>();
  @Output() onSave = new EventEmitter<void>();

  itdcrAttachments: ItdcrAttachmentItem[] = [];
  attachmentGroups: AttachmentGroup[] = []; // Grouped for accordion
  newAttachments: Attachment[] = [];
  attachmentsToDeleteIds: number[] = [];

  constructor(public globals: Globals, private backendService: BackendService, private loaderService: TeiasoftLoaderService, private teiasoftToaster: TeiasoftToasterService, private translate: TranslateService) {

  }

  ngOnInit(): void {
    this.loadAttachments();
  }


  onCloseClick() {
    this.onClose.emit();
  }

  onSaveClick() {
    if (!this.hasPendingChanges) return;
    this.loaderService.start();

    const requests = [];
    if (this.newAttachments && this.newAttachments.length > 0) {
      requests.push(this.backendService.uploadItdcrAttachment(this.itdcrNo, this.newAttachments));
    }
    if (this.attachmentsToDeleteIds && this.attachmentsToDeleteIds.length > 0) {
      requests.push(this.backendService.deleteItdcrAttachment(this.itdcrNo, this.attachmentsToDeleteIds));
    }

    forkJoin(requests).subscribe({
      next: () => {
        this.loaderService.stop();
        this.teiasoftToaster.show("success", this.translate.instant("commons.success"));
        this.onClose.emit();
      },
      error: () => {
        this.loaderService.stop();
      }
    });
  }

  loadAttachments() {
    this.backendService.getAttachmentsForItdcrByItdcr(this.itdcrNo)
      .subscribe(res => {
        const enquiry = (res.enquiryAttachments || []) as EnquiryAttachment[];
        const items = (res.itemsAttachments || []) as ItemAttachment[];
        const itdcr = (res.itdcrAttachments || []) as ItdcrAttachment[];

        //  flat
        this.itdcrAttachments = enquiry.concat(items).concat(itdcr);

        // sort each group by date desc (fallback undefined dates to 0)
        const byDateDesc = (a: ItdcrAttachmentItem, b: ItdcrAttachmentItem) =>
          (new Date(b.fileDate || 0).getTime()) - (new Date(a.fileDate || 0).getTime());

        const groups: AttachmentGroup[] = [
          {
            key: 'enquiry',
            label: this.translate.instant('itdcr.enquiryAttachments') || 'Enquiry attachments',
            items: [...enquiry].sort(byDateDesc)
          },
          {
            key: 'items',
            label: this.translate.instant('itdcr.itemsAttachments') || 'Items attachments',
            items: [...items].sort(byDateDesc)
          },
          {
            key: 'itdcr',
            label: this.translate.instant('itdcr.itdcrAttachments') || 'ITDCR attachments',
            items: [...itdcr].sort(byDateDesc)
          }
        ];

        // show only non-empty groups
        this.attachmentGroups = groups.filter(g => g.items.length > 0);
        this.attachmentsToDeleteIds = [];
      });
  }

  downloadExisting(att: ItdcrAttachmentItem) {
    if (!att.presignedUrl) return;
    window.open(att.presignedUrl, '_blank', 'noopener,noreferrer');
  }

  onAddAttachmentClick() {
    document.getElementById('itdcrFileUploadAttach')?.click();
  }

  // Add files coming from <input type="file" multiple>
  addNewFiles(event: any) {
    const files = Array.from(event.target?.files as any as File[]);

    //  basic constraints
    const MAX_FILES = 20;
    const MAX_SIZE = 25 * 1024 * 1024;   // 25MB each

    for (const f of files) {
      if (f.size > MAX_SIZE) {
        this.teiasoftToaster.show('error', this.translate.instant('commons.fileTooLarge'));
        continue;
      }
      this.readFileAsBase64(f).then(base64 => {
        this.newAttachments.push({
          fileContent: base64,
          fileName: f.name,
          fileCreationDate: f.lastModified || Date.now(),
        });

        event = null;
      });

    }
  }

  trackByAtt = (_: number, att: ItdcrAttachmentItem) =>
    this.getAttachmentId(att) ?? att.fileName ?? att.presignedUrl ?? _;

  removeNewAttachment(index: number) {
    this.newAttachments.splice(index, 1);
  }

  get hasPendingChanges(): boolean {
    return (this.newAttachments?.length ?? 0) > 0 || (this.attachmentsToDeleteIds?.length ?? 0) > 0;
  }

  isMarkedForDeletion(att: ItdcrAttachmentItem): boolean {
    if (!this.isItdcrAttachment(att) || !att.itdcrAttachmentId) return false;
    return this.attachmentsToDeleteIds.includes(att.itdcrAttachmentId);
  }

  toggleDeleteExisting(att: ItdcrAttachment) {
    if (!att.itdcrAttachmentId) return;
    const idx = this.attachmentsToDeleteIds.indexOf(att.itdcrAttachmentId);
    if (idx === -1) {
      this.attachmentsToDeleteIds.push(att.itdcrAttachmentId);
    } else {
      this.attachmentsToDeleteIds.splice(idx, 1);
    }
  }

  isItdcrAttachment(att: ItdcrAttachmentItem): att is ItdcrAttachment {
    return (att as ItdcrAttachment).itdcrAttachmentId != null;
  }

  private getAttachmentId(att: ItdcrAttachmentItem): number | null {
    if ((att as ItdcrAttachment).itdcrAttachmentId != null) {
      return (att as ItdcrAttachment).itdcrAttachmentId ?? null;
    }
    if ((att as EnquiryAttachment).enquiryAttachmentId != null) {
      return (att as EnquiryAttachment).enquiryAttachmentId ?? null;
    }
    if ((att as ItemAttachment).attachmentsId != null) {
      return (att as ItemAttachment).attachmentsId ?? null;
    }
    return null;
  }

  // --- Utils ---
  private stripBase64Header(dataUrl: string): string {
    const comma = dataUrl.indexOf(',');
    return comma >= 0 ? dataUrl.substring(comma + 1) : dataUrl;
  }

  private readFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(this.stripBase64Header(String(reader.result)));
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

}
